import { pipe, ReadonlyArray, Runtime } from "fp";

import * as Log from "~/Log";
import { Reference } from "~/Data";
import { Status } from "~/Driver";
import { Time } from "~/Time";
import { WebAPI } from "~/WebAPI";

export * as Get from "./Get";
export * as Patch from "./Patch";
export * as Post from "./Post";

export const runtime = () =>
  Runtime.intersection([
    Status.Response.runtime(),
    Time.Interval.Stopped.Response.runtime(),
    Runtime.strict({
      distance: Runtime.nullable(Runtime.number()),
      location: Runtime.string(),
      notes: Runtime.nullable(Runtime.string()),
      revision_suggestion_id: Runtime.number(),
      job_id: Runtime.nullable(Runtime.number()),
      work_order_id: Runtime.nullable(Runtime.number()),
      work_code_ids: Runtime.nullable(Runtime.array(Runtime.number())),
    }),
  ]);

export const runtimeAsArray = () => Runtime.array(runtime());

export const decode = (driver: Reference.Reference) => (
  revision: Runtime.ToType<typeof runtime>
): Log.Revision.Revision => ({
  ...Time.Interval.Stopped.Response.decode(revision),
  driver,
  id: `${revision.revision_suggestion_id}`,
  sources: { current: Log.Event.Source.revision() },
  status: Status.Response.decode(revision),
  location: revision.location,
  distance: revision.distance ?? 0,
  notes: revision.notes ?? undefined,
  job: revision.job_id ? { id: `${revision.job_id}` } : undefined,
  workCodes: revision.work_code_ids && revision.work_code_ids.length
    ? revision.work_code_ids.map( id => ({ id: `${id}` }) )
    : undefined,
  workOrder: revision.work_order_id
    ? { id: `${revision.work_order_id}` }
    : undefined,
});

export const decodeArray = (driver: Reference.Reference) => (
  response: Runtime.ToType<typeof runtimeAsArray>
): readonly Log.Revision.Revision[] =>
  pipe(
    response,
    ReadonlyArray.map((revision) => decode(driver)(revision))
  );

/**
 * GQL responses are already typed by Apollo/the schema, so unlike `decode` above
 * there's no `Runtime` validation step — just a mapping into the existing
 * `Log.Revision.Revision` shape. `id` is the internal UUID (or, for a suggestion
 * with no local `eld_events` record, the stringified legacy ES API id) rather than
 * `${revision_suggestion_id}` — see this MR's description for what that changes for
 * `Patch`'s cancel action.
 */
export const decodeFromGQL = (driver: Reference.Reference) => (
  revision: WebAPI.Revision.RevisionSuggestionFragment
): Log.Revision.Revision => ({
  ...Time.Interval.Stopped.fromDateTimes(
    Time.DateTime.fromISOUnsafe(revision.startTime),
    Time.DateTime.fromISOUnsafe(revision.endTime)
  ),
  driver,
  id: revision.revisionSuggestionId,
  sources: { current: Log.Event.Source.revision() },
  status: Status.Response.decode({
    status: revision.status,
    // The schema only ever populates this from the same YM/PU domain the REST
    // path used — narrow rather than cast, so an unexpected value degrades to
    // "no exception" instead of lying to the type checker.
    exception_type:
      revision.exceptionType === "YM" || revision.exceptionType === "PU"
        ? revision.exceptionType
        : null,
  }),
  location: revision.location ?? "",
  distance: revision.distance ?? 0,
  notes: revision.notes ?? undefined,
  job: revision.jobId ? { id: `${revision.jobId}` } : undefined,
  workCodes: revision.workCodeIds && revision.workCodeIds.length
    ? revision.workCodeIds.map( id => ({ id: `${id}` }) )
    : undefined,
  workOrder: revision.workOrderId
    ? { id: `${revision.workOrderId}` }
    : undefined,
});

export const decodeArrayFromGQL = (driver: Reference.Reference) => (
  response: readonly WebAPI.Revision.RevisionSuggestionFragment[]
): readonly Log.Revision.Revision[] =>
  pipe(
    response,
    ReadonlyArray.map((revision) => decodeFromGQL(driver)(revision))
  );
